import React, { useRef } from 'react'
import { invert } from 'lodash'

import { useAppSelector } from 'redux/hooks'

import { HUMAN_OWNED, HUMAN_PINNED_STATUS } from 'types/deck'

import CursorCardWrapper from 'components/card/CursorCardWrapper'
import { cardManaCostToElement } from 'components/misc/customIcons/ManaSymbol/helpers'
import DraggableBasicCard from 'components/card/DraggableBasicCard'
import ColorTagCssDropdown from 'components/card/controlsAndInfo/colorTagCssDropdown'
import DeckCardWrapper, { DeckCardWrapperChildProps } from 'components/card/deckCards/deckCardWrapper'
import Dropdown from 'components/formElements/Dropdown'
import AsyncEditionDropdown from 'components/cardSearchPanel/searchFormElements/asyncEditionDropdown'
import SimpleCategorySelection from '../controlsAndInfo/simpleCategorySelection'
import Icon from 'components/elements/Icon'
import ContextMenu from 'components/elements/ContextMenu'

import { generateContextOptions } from 'components/card/deckCards/deckCardContextMenu'
import { getPriceStringAndUrl } from 'utils/Price'
import { setCodeToElement } from 'components/misc/customIcons/SetSymbols'

import styles from './spreadsheetCard.module.scss'

type Props = {
  cardId: string
  stackName?: string
}

const SpreadsheetCard = ({ cardId, stackName }: Props) => (
  <DeckCardWrapper cardId={cardId} stackName={stackName} component={Card} />
)

export default SpreadsheetCard

const Card = ({ card, stackName, localCardData, deckData, userData, actions }: DeckCardWrapperChildProps) => {
  const qtyInputRef = useRef<any>()

  const priceSource = useAppSelector(state => state.active.priceSource)
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
  const isMobile = useAppSelector(state => state.active.isMobile)
  const disableDeckCursorCard = useAppSelector(state => state.cacheables.disableDeckCursorCard)

  const canViewDragHandles = localCardData.ownsDeck && !isMobile
  const priceObjOne = getPriceStringAndUrl(card, priceSource[0], card.modifier !== 'Normal')
  const priceObjTwo = getPriceStringAndUrl(card, priceSource[1], card.modifier !== 'Normal')
  const collectionStatus = invert(HUMAN_OWNED)[card.owned]
  const pinnedStatus = invert(HUMAN_PINNED_STATUS)[card.pinnedStatus]

  const handleFocusQtyInput = () => {
    qtyInputRef.current?.select()
  }

  return (
    <div
      className={`
      ${styles.card}
      ${localCardData.cardError && styles.cardError}
      ${localCardData.isMultiSelected && styles.selected}
      ${localCardData.antiHighlight && styles.antiHighlight}
    `}>
      {canViewDragHandles && (
        <DraggableBasicCard
          card={card}
          dragItem={{
            card,
            stackName,
            multiSelectedIds: localCardData.isMultiSelected ? multiSelectedIds : undefined,
          }}>
          <span className={styles.dragHandle}>
            <Icon name="bars" />
          </span>
        </DraggableBasicCard>
      )}

      <div className={styles.quantity}>
        <input
          ref={qtyInputRef}
          value={localCardData.quantity}
          onChange={e => actions.setLocalQuantity(Number(e.target.value))}
          className={styles.quantityInput}
          type="number"
          min={0}
          disabled={localCardData.anyMultiSelected}
          onFocus={handleFocusQtyInput}
          onBlur={() =>
            localCardData.quantity
              ? actions.onCardUpdate({ ...card, qty: localCardData.quantity })
              : actions.onCardUpdate([], card.id)
          }
        />
      </div>

      <button className={styles.cardName} onClick={actions.cardClick} onContextMenu={actions.rightClick}>
        <CursorCardWrapper
          skipOnTouch
          className={styles.cursorCard}
          flipped={localCardData.flipped}
          card={card}
          disabled={disableDeckCursorCard}>
          <span
            className={`${styles.cardNameContainer} ${localCardData.inSecondaryCategory ? styles.dimmed : undefined}`}>
            <span>{card.name}</span>
            {card.displayName && (
              <span className={`${styles.displayName} ${card.lang === 'ph' ? styles.phyrexian : ''}`}>
                ({card.displayName})
              </span>
            )}
          </span>
        </CursorCardWrapper>
      </button>

      <div className={styles.modifier}>
        <Dropdown
          value={card.modifier}
          disabled={card.options.length < 2 || localCardData.anyMultiSelected}
          onChange={modifier => actions.onCardUpdate({ ...card, modifier })}
          options={card.options.map(o => ({ value: o, label: o }))}
        />
      </div>
      <div className={styles.manaCost}>{cardManaCostToElement(card, styles.manaCost)}</div>

      <a href={priceObjOne.url} className={styles.shoppingUrl} rel="noopener noreferrer" target="_blank">
        {priceObjOne.price}
      </a>

      <a href={priceObjTwo.url} className={styles.shoppingUrl} rel="noopener noreferrer" target="_blank">
        {priceObjTwo.price}
      </a>

      <div className={styles.collectionStatus}>{collectionStatus}</div>
      <div className={styles.pinnedStatus}>{pinnedStatus}</div>
      <div className={styles.colorTag}>
        <ColorTagCssDropdown
          includeLabel
          card={card}
          onCardChange={actions.onCardUpdate}
          disabled={localCardData.anyMultiSelected}
        />
      </div>
      <div className={styles.setSymbol}>{setCodeToElement(card.setCode, card.rarity, styles.setSymbol)}</div>
      <div className={styles.setName}>
        <AsyncEditionDropdown
          card={card}
          menuClassName={styles.setName}
          triggerClassName={styles.setDropdownTrigger}
          disabled={localCardData.anyMultiSelected}
          onCardSelected={actions.onCardUpdate}
        />
      </div>
      <div className={styles.categories}>
        <SimpleCategorySelection
          disabled={localCardData.anyMultiSelected}
          card={card}
          onCardUpdated={actions.onCardUpdate}
        />
      </div>
      <div className={styles.companion}>
        {localCardData.canBeCompanion && (
          <Dropdown
            value={card.companion || false}
            disabled={card.options.length < 2 || localCardData.anyMultiSelected}
            options={[
              { label: 'Companion', value: true },
              { label: 'Not Companion', value: false },
            ]}
            onChange={companion => actions.onCardUpdate({ ...card, companion })}
          />
        )}
      </div>

      <div className={styles.menuTrigger}>
        <ContextMenu
          openOnLeftClick
          triggerClassName={localCardData.anyMultiSelected ? styles.hideDropdown : undefined}
          menuClassName={styles.dropdown}
          options={generateContextOptions({
            card,
            localCardData,
            actions,
            stackName,
            deckData,
            extraData: {},
            userData,
          })}>
          <button className={styles.extraOptionsTrigger}>
            <Icon name="ellipsis vertical" />
          </button>
        </ContextMenu>
      </div>
    </div>
  )
}
